/** SSE hook for the cohort channel (cohortStream). Reports its health; `onUnhealthy` is the WP40 seam. */
import { useEffect, useRef, useState } from "react";
import type { SseEvent } from "@bsim/contracts";
import type { StreamHealth } from "./context";
import { SSE_UNHEALTHY_AFTER_MS } from "./sse-health";

export function useCohortStream(
  cohortId: string,
  onEvent: (e: SseEvent) => void,
  opts: { readonly onUnhealthy?: (lastEventAt: Date | null) => void } = {},
): StreamHealth {
  const [health, setHealth] = useState<StreamHealth>("open");
  const handlers = useRef({ onEvent, onUnhealthy: opts.onUnhealthy });
  handlers.current = { onEvent, onUnhealthy: opts.onUnhealthy };

  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    let lastEventAt: Date | null = null;
    let downTimer: ReturnType<typeof setTimeout> | undefined;
    const source = new EventSource(`/api/v1/cohorts/${encodeURIComponent(cohortId)}/stream`);
    const markOpen = () => { clearTimeout(downTimer); downTimer = undefined; setHealth("open"); };
    source.onopen = markOpen;
    source.onmessage = (message: MessageEvent<string>) => {
      lastEventAt = new Date();
      markOpen();
      // The api sends each SseEvent as one unnamed message whose data is the event JSON (apps/api CONTEXT, WP13b).
      handlers.current.onEvent(JSON.parse(message.data) as SseEvent);
    };
    source.onerror = () => {
      setHealth((h) => (h === "polling" ? h : "reconnecting"));
      downTimer ??= setTimeout(() => { setHealth("polling"); handlers.current.onUnhealthy?.(lastEventAt); }, SSE_UNHEALTHY_AFTER_MS);
    };
    return () => { clearTimeout(downTimer); source.close(); };
  }, [cohortId]);

  return health;
}
